/** Participants slide-over panel. Opens from the thread header's "Participants" button; loads the conversation's participants on demand. */

import { colors } from '@federa/components/css-refs.colors';
import { spacing } from '@federa/components/css-refs.spacing';
import { typography } from '@federa/components/css-refs.typography';
import { borderRadius, borderWidth, zIndex } from '@federa/components/css-refs.effects';
import { viewport } from '@federa/components/css-refs.layout';
import { Button } from '@federa/components/primitives/Button';
import { BUTTON_HOVER_FILL_NONE } from '@federa/components/primitives/contracts/button';
import { CloseButton } from '@federa/components/primitives/CloseButton';
import { DEFAULT_CLOSE_LABEL } from '@federa/const/aria-default-labels';
import { EMPTY_STATE_PLACEHOLDERS } from '@federa/const/ui-empty-state-placeholders';
import { FULL_HEIGHT_STYLE } from '@federa/const/style-size-presets';
import { FLEX_BETWEEN_STYLE, FLEX_COLUMN_STYLE, FLEX_FILL_SCROLL_Y_STYLE } from '@federa/const/style-flex-presets';
import { CHILDREN_LAYOUT_CONTENT } from '@federa/const/children-layout-values';
import { viewerVar } from './chats-viewer-tokens.ts';
import { prettyLabel } from './format.ts';
import type { CSSProperties } from 'react';
import type { ChatViewerParticipant } from '@federa/types/conversations';
import type { PartData } from '@federa/types/spa-chats-shapes';

interface Props {
	parts: PartData;
	onClose: () => void;
}

const scrim: CSSProperties = {
	position: 'fixed',
	inset: 0,
	background: colors.overlay.medium,
	display: 'flex',
	justifyContent: 'flex-end',
	zIndex: zIndex.floating,
};
/** ONLY the placement. The transparent fill, the border removal, the zero padding and the pointer cursor all come from
 *  `Button` now -- `variant="ghost"` answers the first two, `childrenLayout="content"` drops the label box that carried
 *  the padding and radius, and the pointer affordance is part of every button's behavioural contract. */
const backdropBtn: CSSProperties = {
	position: 'absolute',
	inset: 0,
	margin: 0,
};
const panel: CSSProperties = {
	position: 'relative',
	zIndex: zIndex.raised,
	width: spacing[96],
	maxWidth: viewport.widthLarge,
	...FULL_HEIGHT_STYLE,
	background: viewerVar('--surface'),
	boxShadow: viewerVar('--shadow-lg'),
	...FLEX_COLUMN_STYLE,
};
const head: CSSProperties = {
	...FLEX_BETWEEN_STYLE,
	padding: `${spacing[4]} ${spacing[5]}`,
	borderBottom: `${borderWidth.DEFAULT} solid ${viewerVar('--border')}`,
};
const headTitle: CSSProperties = {
	fontSize: typography.fontSize.base,
	fontWeight: typography.fontWeight.bold,
};
const closeBtn: CSSProperties = {
	border: `${borderWidth.DEFAULT} solid ${viewerVar('--border')}`,
	background: viewerVar('--surface'),
	borderRadius: viewerVar('--radius-sm'),
	width: spacing[7],
	height: spacing[7],
	cursor: 'pointer',
	fontSize: typography.fontSize.base,
	lineHeight: typography.lineHeight.none,
	color: viewerVar('--text-muted'),
};
const list: CSSProperties = { ...FLEX_FILL_SCROLL_Y_STYLE, padding: `${spacing[2]} 0` };
const state: CSSProperties = {
	padding: `${spacing[6]} ${spacing[5]}`,
	color: viewerVar('--text-muted'),
	fontSize: typography.hierarchy.tertiary.size,
};
const card: CSSProperties = {
	padding: `${spacing[3]} ${spacing[5]}`,
	borderBottom: `${borderWidth.DEFAULT} solid ${viewerVar('--border')}`,
};
const nameRow: CSSProperties = { ...FLEX_BETWEEN_STYLE, gap: spacing[2] };
const nameStyle: CSSProperties = { fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.sm };
const activeTag = (active: boolean): CSSProperties => ({
	fontSize: typography.fontSize.xs,
	fontWeight: typography.fontWeight.semibold,
	padding: `${spacing[0.25]} ${spacing[2]}`,
	borderRadius: borderRadius.shapes.tag,
	color: active ? viewerVar('--human-bubble') : viewerVar('--text-faint'),
	background: active ? viewerVar('--human-bubble-soft') : viewerVar('--surface-2'),
});
const detail: CSSProperties = {
	fontSize: typography.fontSize.xs,
	color: viewerVar('--text-muted'),
	marginTop: spacing[0.75],
};

function ParticipantCard({ participant }: { participant: ChatViewerParticipant }) {
	return (
		<div style={card}>
			<div style={nameRow}>
				<span style={nameStyle}>{participant.name ?? EMPTY_STATE_PLACEHOLDERS.UNKNOWN}</span>
				<span style={activeTag(participant.isActive)}>{participant.isActive ? 'Active' : 'Inactive'}</span>
			</div>
			{participant.role && <div style={detail}>{prettyLabel(participant.role)}</div>}
			{participant.userId && <div style={{ ...detail, color: viewerVar('--text-faint') }}>{participant.userId}</div>}
		</div>
	);
}

export function ParticipantsPanel({ parts, onClose }: Props) {
	if (!parts.open) return null;
	return (
		<div style={scrim}>
			{/* A full-bleed dismissal hit area: it owns its own box and must not repaint under the pointer, so the ghost
			    variant's hover layer is suppressed outright rather than tinting an invisible backdrop. */}
			<Button
				type="button"
				variant="ghost"
				childrenLayout={CHILDREN_LAYOUT_CONTENT}
				hoverFill={BUTTON_HOVER_FILL_NONE}
				style={backdropBtn}
				aria-label="Close participants"
				onClick={onClose}
			/>
			<div style={panel}>
				<div style={head}>
					<span style={headTitle}>Participants</span>
					<CloseButton onClick={onClose} size="sm" style={closeBtn} aria-label={DEFAULT_CLOSE_LABEL} />
				</div>
				{parts.error ? (
					<div style={state}>{parts.error}</div>
				) : parts.loading ? (
					<div style={state}>Loading participants...</div>
				) : parts.items.length === 0 ? (
					<div style={state}>No participants found.</div>
				) : (
					<div style={list}>
						{parts.items.map((p) => (
							<ParticipantCard key={p.id} participant={p} />
						))}
					</div>
				)}
			</div>
		</div>
	);
}
